import React, { useEffect, useMemo } from "react";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import type { UploadEntry } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import { CALL_ADDITIONAL_DOCUMENT_TYPES } from "@features/vessel-call/create/constants";
import { t } from "i18next";
import { FormProvider } from "react-hook-form";
import type { Path, UseFormReturn } from "react-hook-form";
import { FORM_FIELDS } from "../../constants";
import type { RequestDepartureFormData } from "../../schema/requestDepartureFormSchema";

type AdditionalDocumentType =
  | "CERTIFICATE"
  | "PERMIT"
  | "DOCUMENT"
  | "INSURANCE"
  | "PRE_SAILING_MASTER_DECLARATION"
  | "VESSEL_MASTER_SOF"
  | "OTHER_SPECIFY";

type AdditionalDocument = {
  name: string;
  url: string;
  type?: string;
  documentType?: AdditionalDocumentType | "";
  otherSpecifyType?: string;
  documentName?: string;
  issueDate?: Date | string | null;
  expiryDate?: Date | string | null;
  documentSummary?: string;
};

type DepartureDocumentProps = {
  testId: string;
  langBase: string;
  formData: UseFormReturn<RequestDepartureFormData>;
  documentType: Path<RequestDepartureFormData>;
  withRedirectButton?: boolean;
  files: UploadEntry[];
  setFiles: (files: UploadEntry[] | ((prev: UploadEntry[]) => UploadEntry[])) => void;
  handleFieldChange: (field: Path<RequestDepartureFormData>, files: File[]) => void;
  removeFn: (index?: number) => void;
  onFieldChange?: (key: string, value: unknown) => void;
  notSuccess?: boolean;
  maxFiles?: number;
  isLoading?: boolean;
  isFileLoading?: (file: UploadEntry, index: number) => boolean;
};

const DepartureDocument: React.FC<DepartureDocumentProps> = ({
  testId,
  langBase,
  formData,
  documentType,
  files,
  setFiles,
  handleFieldChange,
  removeFn,
  onFieldChange,
  notSuccess,
  maxFiles = 1,
  isLoading,
  isFileLoading,
}) => {
  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const isAdditionalDocuments = documentType === FORM_FIELDS.ADDITIONAL_DOCUMENTS;
  const profile = isAdditionalDocuments
    ? FILE_UPLOAD_PROFILES.additionalDocuments
    : FILE_UPLOAD_PROFILES.departureRequestFile;
  const watchedAdditionalDocumentsRaw = formData.watch(FORM_FIELDS.ADDITIONAL_DOCUMENTS as any) as
    | AdditionalDocument[]
    | undefined;
  const watchedAdditionalDocuments = useMemo(
    () =>
      watchedAdditionalDocumentsRaw ?? formData.getValues(FORM_FIELDS.ADDITIONAL_DOCUMENTS) ?? [],
    [formData, watchedAdditionalDocumentsRaw],
  );

  const documentTypeOptions = [
    {
      value: CALL_ADDITIONAL_DOCUMENT_TYPES.CERTIFICATE,
      label: t("vessel-call.additional-documents.document-types.certificate"),
    },
    {
      value: CALL_ADDITIONAL_DOCUMENT_TYPES.PERMIT,
      label: t("vessel-call.additional-documents.document-types.permit"),
    },
    {
      value: CALL_ADDITIONAL_DOCUMENT_TYPES.DOCUMENT,
      label: t("vessel-call.additional-documents.document-types.document"),
    },
    {
      value: CALL_ADDITIONAL_DOCUMENT_TYPES.VESSEL_MASTER_SOF,
      label: t("vessel-call.additional-documents.document-types.vessel-master-sof"),
    },
    {
      value: CALL_ADDITIONAL_DOCUMENT_TYPES.PRE_SAILING_MASTER_DECLARATION,
      label: t("vessel-call.additional-documents.document-types.pre-sailing-master-declaration"),
    },
    {
      value: CALL_ADDITIONAL_DOCUMENT_TYPES.INSURANCE,
      label: t("vessel-call.additional-documents.document-types.insurance"),
    },
    {
      value: CALL_ADDITIONAL_DOCUMENT_TYPES.OTHER_SPECIFY,
      label: t("vessel-call.additional-documents.document-types.other-specify"),
    },
  ];

  const getIssueDateMin = (value?: Date | string | null) => {
    if (!value) return undefined;
    const parsed = value instanceof Date ? value : new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  };
  const OTHER_SPECIFY_LABEL_KEY = "vessel-call.additional-documents.other-specify";

  const hasDateValue = (value: unknown): boolean => {
    if (value == null) return false;
    if (typeof value === "string") return value.trim().length > 0;
    return true;
  };

  const pickNonEmptyString = (primary: unknown, fallback: string | undefined): string => {
    if (typeof primary === "string" && primary.trim().length > 0) {
      return primary;
    }

    return fallback ?? "";
  };

  const pickDateValue = (
    primary: AdditionalDocument["issueDate"] | AdditionalDocument["expiryDate"],
    fallback: AdditionalDocument["issueDate"] | AdditionalDocument["expiryDate"],
  ): AdditionalDocument["issueDate"] | AdditionalDocument["expiryDate"] => {
    if (hasDateValue(primary)) return primary;
    if (hasDateValue(fallback)) return fallback;
    return null;
  };

  const syncMetadataToUploadStore = (idx: number, patch: Partial<AdditionalDocument>) => {
    setFiles((prev) =>
      prev.map((entry, entryIdx) => {
        if (entryIdx !== idx || entry instanceof File) {
          return entry;
        }

        return {
          ...(entry as UploadEntry),
          ...patch,
        } as UploadEntry;
      }),
    );
  };

  const allowedExtensions = profile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );
  const uploadDescription = isAdditionalDocuments
    ? t("upload.additional-documents-upload", {
        extensions: profile.format.join(", "),
        size: profile.maxSizeLabel,
      })
    : t(`${langBase}.uploadDescription`);

  const validateAdditionalDocumentUploadFiles = (incomingFiles: File[]) => {
    if (!isAdditionalDocuments || files.length + incomingFiles.length <= maxFiles) {
      return null;
    }

    return t("form-validation.max-images", { max: maxFiles });
  };

  const validateAdditionalDocumentDrop = (selectedCount: number) => {
    if (!isAdditionalDocuments || files.length + selectedCount <= maxFiles) {
      return null;
    }

    return t("form-validation.max-images", { max: maxFiles });
  };

  const triggerAdditionalDocumentTypeValidation = (index: number) => {
    void formData.trigger([
      `${FORM_FIELDS.ADDITIONAL_DOCUMENTS}.${index}.${FORM_FIELDS.DOCUMENT_TYPE}`,
      `${FORM_FIELDS.ADDITIONAL_DOCUMENTS}.${index}.${FORM_FIELDS.OTHER_SPECIFY_TYPE}`,
      `${FORM_FIELDS.ADDITIONAL_DOCUMENTS}.${index}.${FORM_FIELDS.EXPIRY_DATE}`,
    ] as any);
  };

  const triggerAdditionalDocumentDateValidation = (index: number) => {
    void formData.trigger([
      `${FORM_FIELDS.ADDITIONAL_DOCUMENTS}.${index}.${FORM_FIELDS.ISSUE_DATE}`,
      `${FORM_FIELDS.ADDITIONAL_DOCUMENTS}.${index}.${FORM_FIELDS.EXPIRY_DATE}`,
    ] as any);
  };

  const handleMetadataFieldChange = (key: string, value: unknown) => {
    onFieldChange?.(key, value);

    const match = new RegExp(
      `^${FORM_FIELDS.ADDITIONAL_DOCUMENTS}\.(\\d+)\.(documentType|otherSpecifyType|documentName|issueDate|expiryDate|documentSummary)$`,
    ).exec(key);

    if (!match) return;

    const index = Number(match[1]);
    const field = match[2] as keyof Pick<
      AdditionalDocument,
      | "documentType"
      | "otherSpecifyType"
      | "documentName"
      | "issueDate"
      | "expiryDate"
      | "documentSummary"
    >;

    const patch: Partial<AdditionalDocument> = {
      [field]: value,
    };

    if (field === "documentType" && value !== CALL_ADDITIONAL_DOCUMENT_TYPES.OTHER_SPECIFY) {
      const otherSpecifyPath = `${FORM_FIELDS.ADDITIONAL_DOCUMENTS}.${index}.${FORM_FIELDS.OTHER_SPECIFY_TYPE}`;
      formData.setValue(otherSpecifyPath as any, "", {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
      onFieldChange?.(otherSpecifyPath, "");
      patch.otherSpecifyType = "";
    }

    syncMetadataToUploadStore(index, patch);

    if (field === "documentType") {
      triggerAdditionalDocumentTypeValidation(index);
    }

    if (field === "issueDate" || field === "expiryDate") {
      triggerAdditionalDocumentDateValidation(index);
    }
  };

  useEffect(() => {
    if (!isAdditionalDocuments) return;

    setFiles((prev) => {
      let changed = false;

      const next = prev.map((entry, idx) => {
        if (entry instanceof File) {
          return entry;
        }

        const watched = watchedAdditionalDocuments[idx];
        if (!watched || watched instanceof File) {
          return entry;
        }

        const current = entry as AdditionalDocument;
        const documentType = pickNonEmptyString(watched.documentType, current.documentType);
        const otherSpecifyType =
          documentType === CALL_ADDITIONAL_DOCUMENT_TYPES.OTHER_SPECIFY
            ? pickNonEmptyString(watched.otherSpecifyType, current.otherSpecifyType)
            : "";

        const merged = {
          ...entry,
          documentType,
          otherSpecifyType,
          documentName: pickNonEmptyString(watched.documentName, current.documentName),
          issueDate: pickDateValue(watched.issueDate, current.issueDate),
          expiryDate: pickDateValue(watched.expiryDate, current.expiryDate),
          documentSummary: pickNonEmptyString(watched.documentSummary, current.documentSummary),
        };

        const hasChanged =
          current.documentType !== merged.documentType ||
          current.otherSpecifyType !== merged.otherSpecifyType ||
          current.documentName !== merged.documentName ||
          current.issueDate !== merged.issueDate ||
          current.expiryDate !== merged.expiryDate ||
          current.documentSummary !== merged.documentSummary;

        if (hasChanged) {
          changed = true;
          return merged;
        }

        return entry;
      });

      return changed ? next : prev;
    });
  }, [isAdditionalDocuments, setFiles, watchedAdditionalDocuments]);

  return (
    <FormProvider {...formData}>
      <FormSection title={t(`${langBase}.form-fields.${String(documentType)}`)}>
        {maxFiles > 1 ? (
          // Multiple Files Upload
          <>
            <div className="col-span-full">
              <FormItem<RequestDepartureFormData, Path<RequestDepartureFormData>>
                name={documentType}
                type="file"
                allowedFileExtensions={allowedExtensions}
                uploadConditions={profile}
                testId={`${testId}-${String(documentType)}`}
                maxFiles={maxFiles}
                setFiles={setFiles}
                files={files}
                onUpload={handleFieldChange}
                uploadDescription={uploadDescription}
                notSuccess={notSuccess}
                fileValidation={validateAdditionalDocumentUploadFiles}
                validateDrop={validateAdditionalDocumentDrop}
              />
            </div>
            {files.length > 0 &&
              files.map((file, index) => (
                <div
                  className="col-span-full grid grid-cols-1 gap-4"
                  key={`${file.name}-${(file as File).lastModified ?? index}`}
                >
                  <div className="flex-1">
                    <SingleFile
                      testId={`${String(documentType)}-${index}`}
                      file={file}
                      currentFileIndex={index}
                      fieldName={documentType}
                      setFiles={() => undefined}
                      removeFn={() => removeFn(index)}
                      isLoading={isFileLoading ? isFileLoading(file, index) : isLoading}
                    />
                  </div>
                  {isAdditionalDocuments &&
                    typeof (file as Record<string, unknown>).url === "string" &&
                    Boolean((file as Record<string, unknown>).url) && (
                      <div className="grid w-full gap-4 grid-cols-1 lg:grid-cols-2">
                        <FormItem<RequestDepartureFormData>
                          testId={`additional-documents-${index}-document-type`}
                          name={
                            `${FORM_FIELDS.ADDITIONAL_DOCUMENTS}.${index}.${FORM_FIELDS.DOCUMENT_TYPE}` as any
                          }
                          type="select"
                          options={documentTypeOptions}
                          label={t("vessel-call.additional-documents.document-type")}
                          onFieldChange={handleMetadataFieldChange as any}
                        />
                        {watchedAdditionalDocuments[index]?.documentType ===
                        CALL_ADDITIONAL_DOCUMENT_TYPES.OTHER_SPECIFY ? (
                          <FormItem<RequestDepartureFormData>
                            testId={`additional-documents-${index}-other-specify-type`}
                            name={
                              `${FORM_FIELDS.ADDITIONAL_DOCUMENTS}.${index}.${FORM_FIELDS.OTHER_SPECIFY_TYPE}` as any
                            }
                            type="text"
                            label={t(OTHER_SPECIFY_LABEL_KEY)}
                            maxChars={50}
                            onFieldChange={handleMetadataFieldChange as any}
                          />
                        ) : (
                          <div className="hidden lg:block" aria-hidden="true" />
                        )}
                        <div className="lg:col-span-2">
                          <FormItem<RequestDepartureFormData>
                            testId={`additional-documents-${index}-document-name`}
                            name={
                              `${FORM_FIELDS.ADDITIONAL_DOCUMENTS}.${index}.${FORM_FIELDS.DOCUMENT_NAME}` as any
                            }
                            type="text"
                            label={t("vessel-call.additional-documents.document-name")}
                            maxChars={50}
                            onFieldChange={handleMetadataFieldChange as any}
                          />
                        </div>
                        <FormItem<RequestDepartureFormData>
                          testId={`additional-documents-${index}-issue-date`}
                          name={
                            `${FORM_FIELDS.ADDITIONAL_DOCUMENTS}.${index}.${FORM_FIELDS.ISSUE_DATE}` as any
                          }
                          type="date-picker"
                          label={t("vessel-call.additional-documents.issue-date")}
                          timePicker={false}
                          dateConstraint="not-future"
                          onFieldChange={(key, value) =>
                            handleMetadataFieldChange(String(key), value)
                          }
                        />
                        <FormItem<RequestDepartureFormData>
                          testId={`additional-documents-${index}-expiry-date`}
                          name={
                            `${FORM_FIELDS.ADDITIONAL_DOCUMENTS}.${index}.${FORM_FIELDS.EXPIRY_DATE}` as any
                          }
                          type="date-picker"
                          label={t("vessel-call.additional-documents.expiry-date")}
                          timePicker={false}
                          dateConstraint="not-past"
                          minDate={getIssueDateMin(watchedAdditionalDocuments[index]?.issueDate)}
                          onFieldChange={(key, value) =>
                            handleMetadataFieldChange(String(key), value)
                          }
                        />
                        <div className="lg:col-span-2">
                          <FormItem<RequestDepartureFormData>
                            testId={`additional-documents-${index}-document-summary`}
                            name={
                              `${FORM_FIELDS.ADDITIONAL_DOCUMENTS}.${index}.${FORM_FIELDS.DOCUMENT_SUMMARY}` as any
                            }
                            type="textarea"
                            label={t("vessel-call.additional-documents.document-summary")}
                            maxChars={300}
                            minRows={3}
                            showCharCounter
                            onFieldChange={handleMetadataFieldChange as any}
                          />
                        </div>
                      </div>
                    )}
                </div>
              ))}
          </>
        ) : (
          // Single File Upload
          <>
            {files.length > 0 ? (
              <div className="col-span-full" key={`${String(documentType)}`}>
                <SingleFile
                  testId={`${String(documentType)}`}
                  file={files[0]}
                  fieldName={documentType}
                  setFiles={() => setFiles([] as UploadEntry[])}
                  removeFn={() => removeFn(0)}
                  isLoading={isLoading}
                />
              </div>
            ) : (
              <div className="col-span-full">
                <FormItem<RequestDepartureFormData, Path<RequestDepartureFormData>>
                  name={documentType}
                  type="file"
                  allowedFileExtensions={allowedExtensions}
                  uploadConditions={profile}
                  testId={`${testId}-${String(documentType)}`}
                  maxFiles={maxFiles}
                  setFiles={setFiles}
                  files={files}
                  onUpload={handleFieldChange}
                  uploadDescription={uploadDescription}
                  notSuccess={notSuccess}
                  fileValidation={validateAdditionalDocumentUploadFiles}
                  validateDrop={validateAdditionalDocumentDrop}
                />
              </div>
            )}
          </>
        )}
      </FormSection>
    </FormProvider>
  );
};

export default DepartureDocument;
